import React, {useEffect, useRef, useState} from "react";
import {ApiError} from "../../api";
import {ErrorBanner} from "../../components";
import {getFinanceBalances, listFinanceActions} from "../../api/admin/finance";
import type {
    FinanceAction, FinanceActionLeg, FinanceActionPage, FinanceActionStep, FinanceBalanceSummary, FinanceMoney,
} from "../../api/admin/finance";
import {
    addFinanceDays, financeDateToUTC, financeDay, financeDayDiff, financeToday, formatFinanceDate, formatFinanceDay,
    formatFinanceDayTitle, formatFinanceMoment, formatFinanceTime,
} from "./financeTime";
import {financeTokenLabel, formatFinanceAmount, formatFinanceRub} from "./financeFormat";
import {useFinanceDialog} from "./useFinanceDialog";

// ДДС по заявкам: одна строка — одно действие, изменившее баланс мерчанта или
// команды. Сверху — балансы на начало и конец того же периода. Строка книги
// Paylord по заявке входит в строку заявки; карточка открывается справа.

const LIMIT = 50;
const SEARCH_DELAY_MS = 350;
export const ACTION_MAX_SEARCH = 100;

/* ─── Период ─── */

// DayRange is a period in whole Moscow days, both ends included.
export interface DayRange { start: string; end: string; }
export type PeriodPreset = "today" | "yesterday" | "7d" | "30d" | "month" | "prev_month";
export const PERIOD_PRESETS: readonly {key: PeriodPreset; label: string}[] = [
    {key: "today", label: "Сегодня"}, {key: "yesterday", label: "Вчера"}, {key: "7d", label: "7 дней"},
    {key: "30d", label: "30 дней"}, {key: "month", label: "Этот месяц"}, {key: "prev_month", label: "Прошлый месяц"},
];

export function presetRange(preset: PeriodPreset, today: string): DayRange {
    const month = `${today.slice(0, 8)}01`;
    switch (preset) {
    case "today": return {start: today, end: today};
    case "yesterday": { const day = addFinanceDays(today, -1); return {start: day, end: day}; }
    case "7d": return {start: addFinanceDays(today, -6), end: today};
    case "30d": return {start: addFinanceDays(today, -29), end: today};
    case "month": return {start: month, end: today};
    case "prev_month": { const end = addFinanceDays(month, -1); return {start: `${end.slice(0, 8)}01`, end}; }
    }
}

export function rangeDays(range: DayRange): number { return financeDayDiff(range.start, range.end) + 1; }

// actionPeriod turns whole Moscow days into one explicit UTC period, so the rows
// and the balances read exactly the same interval. A period that ends today
// ends now.
export function actionPeriod(range: DayRange, now: Date = new Date()): {from: string; to: string} {
    if (range.end < range.start) throw new Error("Конец периода должен быть не раньше начала.");
    const iso = (ms: number): string => new Date(ms).toISOString().replace(".000Z", "Z");
    const from = Date.parse(financeDateToUTC(`${range.start}T00:00`));
    const to = Math.min(Date.parse(financeDateToUTC(`${addFinanceDays(range.end, 1)}T00:00`)), Math.floor(now.getTime() / 1000) * 1000);
    if (to <= from) throw new Error("Период ещё не начался.");
    return {from: iso(from), to: iso(to)};
}

function periodText(range: DayRange): string {
    return range.start === range.end ? formatFinanceDay(range.start) : `${formatFinanceDay(range.start)} — ${formatFinanceDay(range.end)}`;
}

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const MONTHS = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];

function addMonths(month: string, count: number): string {
    return new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1 + count, 1)).toISOString().slice(0, 10);
}
function monthDays(month: string): (string | null)[] {
    const year = Number(month.slice(0, 4)); const index = Number(month.slice(5, 7)) - 1;
    const lead = (new Date(Date.UTC(year, index, 1)).getUTCDay() + 6) % 7;
    const count = new Date(Date.UTC(year, index + 1, 0)).getUTCDate();
    return [...Array<null>(lead).fill(null), ...Array.from({length: count}, (_, i) => `${month.slice(0, 8)}${String(i + 1).padStart(2, "0")}`)];
}

function CalendarIcon(): JSX.Element {
    return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><rect x="2" y="3" width="12" height="11" rx="2" stroke="currentColor" strokeWidth="1.4"/><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>;
}

// PeriodPicker: the whole field opens the calendar. The first click picks the
// start, the second the end; days after today are closed.
export function PeriodPicker({value, today, onChange}: {value: DayRange; today: string; onChange: (range: DayRange) => void}): JSX.Element {
    const [open, setOpen] = useState(false);
    const [start, setStart] = useState<string | null>(null);
    const [end, setEnd] = useState<string | null>(null);
    const [hover, setHover] = useState<string | null>(null);
    const [view, setView] = useState(() => addMonths(value.end, -1));
    const box = useRef<HTMLDivElement>(null);
    const field = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open) return undefined;
        const down = (event: MouseEvent): void => { if (box.current && !box.current.contains(event.target as Node)) setOpen(false); };
        const key = (event: KeyboardEvent): void => {
            if (event.key !== "Escape") return;
            event.preventDefault(); setOpen(false); field.current?.focus();
        };
        document.addEventListener("mousedown", down);
        document.addEventListener("keydown", key);
        return () => { document.removeEventListener("mousedown", down); document.removeEventListener("keydown", key); };
    }, [open]);

    const toggle = (): void => {
        if (open) { setOpen(false); return; }
        setStart(value.start); setEnd(value.end); setHover(null); setView(addMonths(`${value.end.slice(0, 8)}01`, -1)); setOpen(true);
    };
    const pick = (day: string): void => {
        if (!start || end) { setStart(day); setEnd(null); return; }
        if (day < start) { setEnd(start); setStart(day); } else setEnd(day);
    };
    const allowed = (day: string): boolean => day <= today;
    const other = end || (start ? hover : null);
    const lo = start && other && other < start ? other : start;
    const hi = start && other && other > start ? other : start;
    const chosen = start && end ? {start: start < end ? start : end, end: start < end ? end : start} : null;
    const lastMonth = `${today.slice(0, 8)}01`;
    const apply = (): void => { if (!chosen) return; onChange(chosen); setOpen(false); field.current?.focus(); };

    return <div className="fa-picker" ref={box}>
        <button type="button" ref={field} className={`fa-field${open ? " open" : ""}`} aria-haspopup="dialog" aria-expanded={open} onClick={toggle}>
            <CalendarIcon/><span className="fa-field-text">{periodText(value)}</span><span className="fa-field-days">{rangeDays(value)} дн.</span>
        </button>
        {open && <div className="fa-cal" role="dialog" aria-label="Выбор периода">
            <div className="fa-cal-months">
                <button type="button" className="fa-cal-nav fa-cal-prev" aria-label="Предыдущий месяц" onClick={() => setView(addMonths(view, -1))}>‹</button>
                <button type="button" className="fa-cal-nav fa-cal-next" aria-label="Следующий месяц" disabled={addMonths(view, 1) >= lastMonth} onClick={() => setView(addMonths(view, 1))}>›</button>
                {[view, addMonths(view, 1)].map((month) => <div className="fa-cal-month" key={month}>
                    <div className="fa-cal-head"><b>{MONTHS[Number(month.slice(5, 7)) - 1]} {month.slice(0, 4)}</b></div>
                    <div className="fa-cal-grid" onMouseLeave={() => setHover(null)}>
                        {WEEKDAYS.map((day) => <span className="fa-cal-wd" key={day}>{day}</span>)}
                        {monthDays(month).map((day, i) => day === null ? <span key={`e${i}`}/> : <button type="button" key={day}
                            className={["fa-cal-day", lo && hi && day >= lo && day <= hi ? "in" : "", day === lo ? "lo" : "", day === hi ? "hi" : "", day === today ? "today" : ""].filter(Boolean).join(" ")}
                            disabled={!allowed(day)} aria-label={formatFinanceDay(day)} aria-pressed={Boolean(lo && hi && day >= lo && day <= hi)}
                            onClick={() => pick(day)} onMouseEnter={() => setHover(day)}>{Number(day.slice(8))}</button>)}
                    </div>
                </div>)}
            </div>
            <div className="fa-cal-foot">
                <span>{chosen ? `${periodText(chosen)} · ${rangeDays(chosen)} дн.` : start ? `С ${formatFinanceDay(start)} — выберите конец` : "Выберите начало периода"}<small>Москва (UTC+3), дни целиком</small></span>
                <div><button type="button" className="btn btn-ghost btn-sm" onClick={() => { setOpen(false); field.current?.focus(); }}>Отмена</button><button type="button" className="btn btn-primary btn-sm" disabled={!chosen} onClick={apply}>Применить</button></div>
            </div>
        </div>}
    </div>;
}

/* ─── Подписи ─── */

export function actionKindLabel(kind: string): string {
    return ({
        payment: "Заявка", merchant_withdrawal: "Вывод мерчанта", team_withdrawal: "Вывод команды",
        paylord_withdrawal: "Вывод из кабинета Paylord", book_row: "Книга Paylord", book_opening: "Начальный остаток книги Paylord",
        book_row_settlement: "Строка книги, проведённая вручную", platform_closed: "Заявка, закрытая площадкой",
        blockchain_deposit: "Пополнение из блокчейна", team_reset: "Сброс баланса команды", merchant_reset: "Сброс баланса мерчанта",
        debt_correction: "Корректировка Paylord", manual: "Ручная корректировка", backfill: "Перенос истории", other: "Прочее",
    } as Record<string, string>)[kind] || kind;
}

function movementReasonLabel(reason: string): string {
    return ({
        payment_success: "Успешный платёж", team_reset: "Списание по выводу команды", merchant_reset: "Сброс баланса мерчанта",
        manual_topup: "Ручное пополнение", manual_withdrawal: "Ручное списание", blockchain_deposit: "Поступление из блокчейна",
        external_book: "Книга Paylord", external_reimbursement: "Возмещение", external_profit: "Доход", backfill: "Перенос истории",
        backfill_correction: "Корректировка переноса", merchant_withdrawal_reserve: "Резерв вывода", merchant_withdrawal_release: "Возврат резерва",
    } as Record<string, string>)[reason] || reason;
}

export function plural(n: number, forms: [string, string, string]): string {
    const ten = n % 10; const hundred = n % 100;
    return forms[ten === 1 && hundred !== 11 ? 0 : ten >= 2 && ten <= 4 && (hundred < 10 || hundred >= 20) ? 1 : 2];
}

const amount = (value?: FinanceMoney): string => formatFinanceAmount(value);
const signed = (value?: FinanceMoney): string => formatFinanceAmount(value, {sign: true});
const exact = (value?: FinanceMoney): string => formatFinanceAmount(value, {digits: value?.scale ?? 2});
const exactSigned = (value?: FinanceMoney): string => formatFinanceAmount(value, {digits: value?.scale ?? 2, sign: true});
const money = (atomic: string, leg: FinanceActionLeg): FinanceMoney => ({atomic, scale: leg.delta.scale, token: leg.delta.token});
const isPositive = (value?: FinanceMoney): boolean => Boolean(value && !value.atomic.startsWith("-") && /[1-9]/.test(value.atomic));
const isNegative = (atomic?: string): boolean => Boolean(atomic && atomic.startsWith("-") && /[1-9]/.test(atomic));

function difference(a?: FinanceMoney, b?: FinanceMoney): FinanceMoney | undefined {
    if (!a || !b || a.scale !== b.scale) return undefined;
    try { return {atomic: (BigInt(a.atomic) - BigInt(b.atomic)).toString(), scale: a.scale, token: a.token}; } catch { return undefined; }
}

function ownerName(leg?: FinanceActionLeg): string { return leg ? leg.owner.name || leg.owner.id : "—"; }

export function legOwnerLabel(leg: FinanceActionLeg): string {
    const kind = leg.owner.type === "team" ? "Команда" : leg.owner.type === "merchant" ? (leg.settlement_source === "external" ? "Кабинет Paylord" : "Мерчант") : "Баланс";
    return `${kind} ${ownerName(leg)}`;
}

// Balances of teams and our merchants read as debts: plus — we owe the owner,
// minus — the owner owes us. Paylord cabinets mirror the external book and keep
// the sign.
function debtSide(leg: FinanceActionLeg, atomic: string, format: (value?: FinanceMoney) => string): {label: string; value: string} {
    const value = money(atomic, leg);
    if (leg.owner.type !== "team" && (leg.owner.type !== "merchant" || leg.settlement_source === "external")) return {label: "", value: format(value)};
    if (isNegative(atomic)) return {label: `${leg.owner.type === "team" ? "должна" : "должен"} нам`, value: format({...value, atomic: atomic.slice(1)})};
    return {label: isPositive(value) ? "мы должны" : "", value: format(value)};
}

function debtNow(leg: FinanceActionLeg): string {
    if (leg.unknown || leg.after === undefined) return "";
    const side = debtSide(leg, leg.after, amount);
    return side.label ? `, теперь ${side.label} ${side.value}` : "";
}

function LegRange({leg, format = amount}: {leg: FinanceActionLeg; format?: (value?: FinanceMoney) => string}): JSX.Element {
    if (leg.unknown) return <>остаток не сохранён</>;
    if (!leg.contiguous || leg.before === undefined || leg.after === undefined) return <>{leg.chain_broken ? "остаток не восстановлен" : `по шагам · ${leg.steps.length}`}</>;
    const from = debtSide(leg, leg.before, format);
    const to = debtSide(leg, leg.after, format);
    const label = (text: string): React.ReactNode => text && <span className="fa-dw">{text} </span>;
    if (from.label === to.label) return <>{label(from.label)}<span className="mono">{from.value} → {to.value}</span></>;
    return <>{label(from.label)}<span className="mono">{from.value}</span> → {label(to.label)}<span className="mono">{to.value}</span></>;
}

type Tone = "ok" | "warn" | "mut" | "need" | "bad";
interface Pill { tone: Tone; text: string; }
interface Badge { tone: "out" | "in" | "team" | "merchant" | "adj"; text: string; }

function bookExternalID(step: FinanceActionStep): string | undefined {
    const key = step.operation_key || "";
    return /^[a-z0-9_-]+:(\d+):[a-z-]+$/.exec(key)?.[1] || /^debt-correction:[a-z0-9_-]+:order:\d+:(\d+)$/.exec(key)?.[1];
}
function keyOrderNumber(action: FinanceAction): string | undefined {
    for (const step of action.legs.flatMap((leg) => leg.steps)) {
        const match = /^debt-correction:[a-z0-9_-]+:order:(\d+)/.exec(step.operation_key || "");
        if (match) return match[1];
    }
    return undefined;
}
function keyWithdrawalNumber(action: FinanceAction): string | undefined {
    for (const step of action.legs.flatMap((leg) => leg.steps)) {
        const match = /^debt-correction:[a-z0-9_-]+:withdrawal:(\d+)/.exec(step.operation_key || "");
        if (match) return match[1];
    }
    return undefined;
}

function feeWithdrawals(numbers: string[]): string {
    const named = numbers.map((n) => `№${n}`);
    return named.length > 1 ? `выводы ${named.slice(0, -1).join(", ")} и ${named[named.length - 1]}` : `вывод ${named[0]}`;
}

function actorView(action: FinanceAction): {who: string; whoSub?: string} {
    const [source, provider, cabinet] = action.actor.split(":");
    if (source === "extsource") return {who: `Синхронизация ${provider === "paylord" ? "Paylord" : provider}`, whoSub: cabinet ? `кабинет ${cabinet}` : undefined};
    if (source === "backfill") return {who: "Перенос истории", whoSub: action.actor.slice("backfill:".length)};
    if (source === "admin" && provider === "book-row") return {who: action.actor_name, whoSub: "провёл строку книги"};
    return {who: action.actor_name || action.actor};
}

export function actionState(action: FinanceAction): Pill {
    const order = action.order;
    if (order && (action.kind === "payment" || action.kind === "platform_closed")) {
        if (order.payment_status === "success" || order.status === "success") return {tone: "ok", text: action.kind === "platform_closed" ? "Закрыта площадкой" : "Успешно"};
        return {tone: "warn", text: ({cancelled: "Отменена", appeal: "Спор", partially_filled: "Частично", pending: "В работе", new: "Новая"} as Record<string, string>)[order.status] || order.status};
    }
    if (action.kind === "team_withdrawal") return ({completed: {tone: "ok", text: "Завершён"}, cancelled: {tone: "mut", text: "Отменён"}} as Record<string, Pill>)[action.status || ""] || {tone: "warn", text: "Ждёт"};
    if (action.kind === "merchant_withdrawal") {
        return ({completed: {tone: "ok", text: "Выполнен"}, rejected: {tone: "bad", text: "Отклонён"}, requested: {tone: "warn", text: "Ждёт · запрошен"}, accepted: {tone: "warn", text: "Ждёт · принят"}, sending: {tone: "warn", text: "Ждёт · отправляется"}} as Record<string, Pill>)[action.status || ""] || {tone: "warn", text: action.status || "Ждёт"};
    }
    if (action.kind === "blockchain_deposit") return {tone: "ok", text: "Зачислено"};
    return {tone: "ok", text: "Проведено"};
}

export function actionFlags(action: FinanceAction): Pill[] {
    const w = action.team_withdrawal;
    const reset = w?.reset || action.amount;
    const flags: Pill[] = action.notes.flatMap((note): Pill[] => {
        switch (note) {
        case "underpaid": return [{tone: "warn", text: w ? `Недополучено ${amount(difference(reset, w.received))}` : "Недоплата"}];
        case "overpaid": return [{tone: "warn", text: w ? `Переплата ${amount(difference(w.received, reset))}` : "Переплата"}];
        case "closed_manually": return [{tone: "mut", text: "Закрыт вручную"}];
        case "team_waiting_book": return [{tone: "need", text: "Ждёт книгу Paylord"}];
        case "balance_chain_broken": return [{tone: "bad", text: "Остаток не восстановлен"}];
        case "balance_unknown": return [{tone: "mut", text: "Остаток не сохранён"}];
        case "net_zero": return [{tone: "mut", text: action.kind === "merchant_withdrawal" ? "Резерв возвращён" : action.kind === "paylord_withdrawal" ? "Заморозка снята" : "Итог 0"}];
        case "several_currencies": return [{tone: "warn", text: "Несколько валют"}];
        case "team_via_book":
        case "paylord_fee": return [];
        default: return [{tone: "mut", text: note}];
        }
    });
    if (action.team_book?.status === "posted" && !action.team_book.reconciled) flags.push({tone: "warn", text: `Книга расходится на ${amount(action.team_book.difference)}`});
    return flags;
}

export interface ActionView {
    badge: Badge; title: string; sub?: string; who: string; whoSub?: string; amount: string; amountSub?: string;
    legs: FinanceActionLeg[]; balanceSub?: string; balanceSubTone?: Tone; state: Pill; flags: Pill[];
}

// actionView is what one row says: what happened, who did it, how much and
// whose balance moved. The card repeats it and adds the reasons.
export function actionView(action: FinanceAction): ActionView {
    const merchant = action.legs.find((leg) => leg.owner.type === "merchant");
    const team = action.legs.find((leg) => leg.owner.type === "team");
    const token = financeTokenLabel(action.amount.token);
    const base: ActionView = {
        badge: {tone: "adj", text: "ПРОЧЕЕ"}, title: actionKindLabel(action.kind), ...actorView(action),
        amount: `${amount(action.amount)}${token && token !== "USDT" ? ` ${token}` : ""}`, legs: action.legs, state: actionState(action), flags: actionFlags(action),
    };
    const book = action.book_rows?.[0];
    switch (action.kind) {
    case "payment":
    case "platform_closed": {
        const order = action.order;
        const side = order?.side === "in" || (!order && action.direction === "in") ? "in" : "out";
        const tb = action.team_book;
        const view: ActionView = {
            ...base, badge: {tone: side, text: side === "in" ? "IN" : "OUT"},
            title: `Заявка №${action.order_number || order?.number || "—"}${action.kind === "platform_closed" ? " · закрыта площадкой" : ""}`,
            sub: `${ownerName(merchant)} · ${side === "in" ? "оплата от клиента" : "выплата клиенту"}`,
            legs: merchant ? action.legs.filter((leg) => leg.owner.type === "merchant") : action.legs,
        };
        if (order?.trader) { view.who = `Трейдер ${order.trader}`; view.whoSub = [order.team && `команда ${order.team}`, order.appealed_at && "через спор"].filter(Boolean).join(" · ") || undefined; }
        if (order?.fiat) view.amountSub = `${side === "in" ? "клиент перевёл" : "клиенту"} ${formatFinanceRub(order.fiat)}`;
        if (tb?.status === "posted") {
            view.balanceSub = `Книга Paylord: ${tb.team || ownerName(team)} ${signed(tb.amount)} · ${tb.reconciled ? "сходится" : `расходится на ${amount(tb.difference)}`}`;
            view.balanceSubTone = tb.reconciled ? undefined : "warn";
        } else if (tb?.status === "waiting") {
            view.balanceSub = "Книга Paylord: ещё не пришла"; view.balanceSubTone = "need";
        } else if (merchant && team) view.balanceSub = `${legOwnerLabel(team)} ${signed(team.delta)}`;
        return view;
    }
    case "team_withdrawal": {
        const w = action.team_withdrawal;
        const merchants = new Set(team?.steps.map((step) => step.counterparty?.id).filter(Boolean)).size;
        return {
            ...base, badge: {tone: "team", text: "ВЫВОД"}, title: `Вывод команды ${ownerName(team)}`,
            sub: [w?.created_by_name && `запросил ${w.created_by_name}`, merchants > 0 && `заработано на ${merchants} ${plural(merchants, ["мерчанте", "мерчантах", "мерчантах"])}`].filter(Boolean).join(" · ") || undefined,
            who: w?.completed_by_name || w?.completed_by || action.actor_name, whoSub: w?.completed_at ? "завершил запрос" : "запрос не завершён",
            amountSub: w ? `получено ${amount(w.received)}` : undefined,
        };
    }
    case "team_reset": return {...base, badge: {tone: "team", text: "СБРОС"}, title: `Сброс баланса команды ${ownerName(team)}`};
    case "merchant_withdrawal": {
        const w = action.merchant_withdrawal;
        return {
            ...base, badge: {tone: "merchant", text: "ВЫВОД"}, title: `Вывод мерчанта ${ownerName(merchant)}`,
            sub: w ? `${w.network.toUpperCase()} · на адрес мерчанта` : undefined,
            amountSub: w?.received ? `получено ${amount(w.received)}${w.fee ? ` · комиссия ${amount(w.fee)}` : ""}` : undefined,
        };
    }
    case "paylord_withdrawal": {
        const fee = action.paylord_fee?.withdrawals;
        if (fee?.length) return {...base, badge: {tone: "adj", text: "КОМИССИЯ"}, title: `Комиссия Paylord за ${feeWithdrawals(fee)}`, sub: ownerName(merchant)};
        const number = action.book_rows?.find((row) => row.external_withdrawal_id)?.external_withdrawal_id || keyWithdrawalNumber(action);
        return {...base, badge: {tone: "merchant", text: "ВЫВОД"}, title: "Вывод из кабинета Paylord", sub: [ownerName(merchant), number && `вывод №${number}`].filter(Boolean).join(" · ")};
    }
    case "blockchain_deposit": {
        const owner = merchant || team;
        return {...base, badge: {tone: merchant ? "merchant" : "team", text: "ПОПОЛН."}, title: `Пополнение ${ownerName(owner)}`, sub: "поступление в сети", whoSub: "автоматически"};
    }
    case "merchant_reset": return {...base, badge: {tone: "merchant", text: "СБРОС"}, title: `Сброс баланса мерчанта ${ownerName(merchant)}`};
    case "book_row": {
        const type = book?.operation_type || "Строка книги Paylord";
        const order = action.order_number || book?.external_order_id;
        return {
            ...base, badge: {tone: "adj", text: /пересч/i.test(type) ? "ПЕРЕСЧЁТ" : /коррек/i.test(type) ? "КОРР." : "КНИГА"}, title: type,
            sub: [book?.external_id && `строка книги ${book.external_id}`, order ? `заявка №${order}` : "без номера заявки"].filter(Boolean).join(" · "),
        };
    }
    case "book_opening": return {...base, badge: {tone: "adj", text: "НАЧ. ОСТ."}, title: "Начальный остаток книги Paylord"};
    case "book_row_settlement": {
        const step = action.legs.flatMap((leg) => leg.steps).find((s) => bookExternalID(s));
        const order = action.order_number || keyOrderNumber(action);
        return {
            ...base, badge: {tone: "adj", text: "КНИГА"}, title: "Строка книги проведена вручную",
            sub: [step && `строка книги ${bookExternalID(step)}`, order && `заявка №${order}`].filter(Boolean).join(" · ") || undefined,
        };
    }
    case "debt_correction": {
        const order = action.order_number || keyOrderNumber(action);
        const withdrawal = keyWithdrawalNumber(action);
        return {
            ...base, badge: {tone: "adj", text: "КОРР."}, title: `Корректировка Paylord ${signed((merchant || action.legs[0])?.delta)}`,
            sub: [ownerName(merchant || action.legs[0]), order ? `по заявке №${order}` : withdrawal ? `по выводу №${withdrawal}` : undefined].filter(Boolean).join(" · "),
        };
    }
    case "manual": {
        const leg = action.legs[0];
        return {...base, badge: {tone: "adj", text: "КОРР."}, title: isNegative(leg?.delta.atomic) ? "Ручное списание" : "Ручное пополнение", sub: leg ? legOwnerLabel(leg) : undefined};
    }
    case "backfill": return {...base, badge: {tone: "adj", text: "ПЕРЕНОС"}, title: "Перенос истории"};
    default: {
        const step = action.legs[0]?.steps[0];
        return {...base, title: step ? movementReasonLabel(step.reason) : base.title, sub: action.legs.map(legOwnerLabel).join(", ") || undefined};
    }
    }
}

function BadgeView({badge, large = false}: {badge: Badge; large?: boolean}): JSX.Element {
    return <span className={`fa-badge fa-k-${badge.tone}${large ? " fa-badge-lg" : ""}`}>{badge.text}</span>;
}
function PillView({pill}: {pill: Pill}): JSX.Element { return <span className={`fa-pill fa-${pill.tone}`}>{pill.text}</span>; }

/* ─── Вкладка ─── */

const GROUPS: readonly {key: "" | "orders" | "team" | "merchant" | "adjustments"; label: string}[] = [
    {key: "", label: "Все"}, {key: "orders", label: "Заявки"}, {key: "team", label: "Выводы команд"},
    {key: "merchant", label: "Пополнения и выводы мерчантов"}, {key: "adjustments", label: "Корректировки и пересчёты"},
];
const QUICK: readonly PeriodPreset[] = ["today", "7d", "30d", "month", "prev_month"];

interface LoadedPage { from: string; to: string; group: string; q: string; cursor?: string; totals?: {actions: number; movements: number}; }
interface PeriodState { key: string; value?: {from: string; to: string}; error?: unknown; }

export function FinanceActions({token, refresh, onSelect}: {token: string | null; refresh: number; onSelect: (operationID: string) => void}): JSX.Element {
    const today = financeToday();
    const [range, setRange] = useState<DayRange>(() => presetRange("7d", financeToday()));
    const [group, setGroup] = useState("");
    const [search, setSearch] = useState("");
    const [q, setQ] = useState("");
    const [items, setItems] = useState<FinanceAction[]>([]);
    const [page, setPage] = useState<LoadedPage | null>(null);
    const [counts, setCounts] = useState<FinanceActionPage["groups"] | null>(null);
    const [balances, setBalances] = useState<FinanceBalanceSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [balancesLoading, setBalancesLoading] = useState(false);
    const [moreLoading, setMoreLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const [balancesError, setBalancesError] = useState<unknown>(null);
    const [moreError, setMoreError] = useState<unknown>(null);
    const [open, setOpen] = useState<FinanceAction | null>(null);
    const rowsSeq = useRef(0);
    const balancesSeq = useRef(0);
    const period = useRef<PeriodState>({key: ""});

    useEffect(() => {
        const timer = window.setTimeout(() => setQ(search.trim().replace(/^№\s*/, "").slice(0, ACTION_MAX_SEARCH)), SEARCH_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [search]);

    // One period per range and refresh: the rows reread it on a search or a
    // segment change, the balances only when it changes.
    useEffect(() => {
        if (!token) return;
        const key = `${refresh}|${range.start}|${range.end}`;
        const changed = period.current.key !== key;
        if (changed) {
            try { period.current = {key, value: actionPeriod(range)}; } catch (err) { period.current = {key, error: err}; }
        }
        const {value, error: periodError} = period.current;
        const rows = ++rowsSeq.current;
        setMoreLoading(false); setMoreError(null);
        if (!value) {
            ++balancesSeq.current;
            setError(periodError); setItems([]); setPage(null); setCounts(null); setLoading(false);
            setBalances(null); setBalancesError(null); setBalancesLoading(false);
            return;
        }
        setLoading(true); setError(null);
        listFinanceActions(token, {...value, group: group || undefined, q: q || undefined, limit: LIMIT})
            .then((result) => {
                if (rows !== rowsSeq.current) return;
                setItems(result.items || []); setCounts(result.groups || null);
                setPage({from: result.from, to: result.to, group, q, cursor: result.next_cursor, totals: result.totals});
            })
            .catch((err) => { if (rows !== rowsSeq.current) return; setError(err); setItems([]); setPage(null); })
            .finally(() => { if (rows === rowsSeq.current) setLoading(false); });
        if (!changed) return;
        const sums = ++balancesSeq.current;
        setBalancesLoading(true); setBalancesError(null); setBalances(null);
        getFinanceBalances(token, value)
            .then((result) => { if (sums === balancesSeq.current) setBalances(result); })
            .catch((err) => { if (sums === balancesSeq.current) setBalancesError(err); })
            .finally(() => { if (sums === balancesSeq.current) setBalancesLoading(false); });
    }, [token, range, group, q, refresh]);

    // Unmounting drops answers in flight and the period, so a remount (or the
    // development double mount) reads both again.
    useEffect(() => {
        const rows = rowsSeq; const sums = balancesSeq; const current = period;
        return () => { ++rows.current; ++sums.current; current.current = {key: ""}; };
    }, []);

    const loadMore = (): void => {
        if (!token || !page?.cursor || moreLoading) return;
        const rows = rowsSeq.current;
        setMoreLoading(true); setMoreError(null);
        listFinanceActions(token, {from: page.from, to: page.to, group: page.group || undefined, q: page.q || undefined, limit: LIMIT, cursor: page.cursor})
            .then((result) => { if (rows !== rowsSeq.current) return; setItems((old) => [...old, ...(result.items || [])]); setPage((old) => old && {...old, cursor: result.next_cursor}); })
            .catch((err) => { if (rows === rowsSeq.current) setMoreError(err); })
            .finally(() => { if (rows === rowsSeq.current) setMoreLoading(false); });
    };

    const sameRange = (other: DayRange): boolean => other.start === range.start && other.end === range.end;
    const filtered = Boolean(group || q);
    const total = page?.totals?.actions;

    return <div className="fa">
        <div className="fa-period">
            <div className="fa-presets" role="group" aria-label="Быстрый выбор периода">
                {QUICK.map((key) => { const preset = presetRange(key, today); return <button type="button" key={key} className={sameRange(preset) ? "on" : ""} aria-pressed={sameRange(preset)} onClick={() => setRange(preset)}>{PERIOD_PRESETS.find((p) => p.key === key)?.label}</button>; })}
            </div>
            <PeriodPicker value={range} today={today} onChange={setRange}/>
        </div>
        <FinanceBalances summary={balances} error={balancesError} loading={balancesLoading}/>
        <div className="fa-toolbar">
            <div className="fa-seg" role="group" aria-label="Тип действия">
                {GROUPS.map((g) => <button type="button" key={g.key || "all"} className={group === g.key ? "on" : ""} aria-pressed={group === g.key} onClick={() => setGroup(g.key)}>
                    {g.label}{counts && <span>{g.key ? counts[g.key] : counts.all}</span>}
                </button>)}
            </div>
            <input type="search" className="fa-search" placeholder="№ заявки, команда, трейдер, мерчант" aria-label="Поиск по заявке, команде, трейдеру или мерчанту" maxLength={ACTION_MAX_SEARCH} value={search} onChange={(event) => setSearch(event.target.value)}/>
        </div>
        <ErrorBanner error={error} title={error instanceof ApiError && error.status === 403 ? "Нет доступа к финансовому разделу" : "Не удалось загрузить ДДС"}/>
        {loading && !items.length && !error && <div className="empty-state" role="status" aria-live="polite">Загрузка ДДС...</div>}
        {!error && (!loading || items.length > 0) && <>
            <ActionsFeed items={items} busy={loading} onOpen={setOpen} empty={filtered ? "Ничего не найдено" : "За период действий нет"}/>
            <div className="fa-foot">
                <span>{items.length > 0 && <>Показано {items.length}{total !== undefined && total > items.length ? ` из ${total}` : ""}. </>}Одна строка — одно действие; проводки и ID — в карточке.</span>
                <span>Рубли идут с карты на карту вне системы и видны только в подписи.</span>
            </div>
            <ErrorBanner error={moreError} title="Не удалось загрузить следующую страницу"/>
            {page?.cursor && <div className="finance-more"><button className="btn btn-ghost" onClick={loadMore} disabled={moreLoading}>{moreLoading ? "Загрузка..." : "Загрузить ещё"}</button></div>}
        </>}
        {open && <ActionDrawer action={open} onClose={() => setOpen(null)} onOpenOperation={(id) => { setOpen(null); onSelect(id); }}/>}
    </div>;
}

/* ─── Плитки ─── */

function compareAtomic(a: FinanceMoney, b: FinanceMoney): number {
    try { const d = BigInt(a.atomic) - BigInt(b.atomic); return d > 0n ? 1 : d < 0n ? -1 : 0; } catch { return 0; }
}

function balanceOwnerKind(item: FinanceBalanceSummary["items"][number]): string {
    if (item.owner.type === "team") return "Команда";
    return ({external: "Кабинет Paylord", external_debt: "Мерчант, режим долга"} as Record<string, string>)[item.settlement_source || ""] || "Мерчант";
}

export function FinanceBalances({summary, error, loading}: {summary: FinanceBalanceSummary | null; error: unknown; loading: boolean}): JSX.Element | null {
    if (error) return <ErrorBanner error={error} title="Не удалось загрузить балансы"/>;
    if (!summary) return loading ? <div className="fa-tiles" aria-busy="true" aria-label="Загрузка балансов">{[0, 1].map((i) => <div className="fa-tile fa-tile-skeleton" key={i}/>)}</div> : null;
    const zero: FinanceMoney = {atomic: "0", scale: 6, token: summary.token};
    const sides = {positive: zero, positive_count: 0, negative: zero, negative_count: 0};
    const group = (key: "merchants" | "teams"): {opening: typeof sides; closing: typeof sides} => summary.groups.find((g) => g.key === key) || {opening: sides, closing: sides};
    const merchants = group("merchants"); const teams = group("teams");
    const teamItems = summary.items.filter((item) => item.owner.type === "team" && item.closing.atomic !== "0").sort((a, b) => compareAtomic(b.closing, a.closing));
    const rows = summary.items;
    return <section className="fa-balances" aria-label="Балансы">
        <div className="fa-tiles">
            <div className="fa-tile fa-tile-m">
                <span className="fa-cap">Мерчанты · наш учёт</span>
                <div className="fa-two">
                    <div><small>Мы должны мерчантам</small><strong>{amount(merchants.closing.positive)}</strong></div>
                    <div><small>Мерчанты должны нам</small><strong>{amount(merchants.closing.negative)}</strong></div>
                </div>
                <span className="fa-hint">На начало: {amount(merchants.opening.positive)} и {amount(merchants.opening.negative)}. Долг не вычитаем из остатков.</span>
            </div>
            <div className="fa-tile fa-tile-t">
                <span className="fa-cap">Команды · наш учёт</span>
                <strong>{amount(teams.closing.positive)}<i>{summary.token}</i></strong>
                {teamItems.length > 0 && <span className="fa-sub">{teamItems.map((item) => `${item.owner.name || item.owner.id} ${amount(item.closing)}`).join(" · ")}</span>}
                <span className="fa-hint">Сколько мы должны командам. На начало: {amount(teams.opening.positive)}.{teams.closing.negative_count > 0 && <span className="fa-warn-text"> Команды должны нам: {amount(teams.closing.negative)}.</span>}</span>
            </div>
        </div>
        <p className="fa-measure">Балансы на {formatFinanceDate(summary.to)} МСК, на начало — {formatFinanceDate(summary.from)}. Только {summary.token}. Архивные мерчанты не входят. Разные меры не складываем.{summary.other_currencies > 0 && ` Балансов в других валютах: ${summary.other_currencies}, в итоги не входят.`}</p>
        {rows.length > 0 && <details className="fa-fold fa-balance-items"><summary>По каждому балансу <span>· {rows.length}</span></summary>
            <div className="table-wrapper fa-balance-table"><table>
                <thead><tr><th>Имя</th><th>ID</th><th>Тип</th><th className="fa-num">Баланс, {summary.token}</th></tr></thead>
                <tbody>{rows.map((item) => <tr key={`${item.owner.type}:${item.owner.id}`}>
                    <td>{item.owner.name || item.owner.id}</td>
                    <td className="mono">{item.owner.id}</td>
                    <td>{balanceOwnerKind(item)}</td>
                    <td className="mono fa-num">{amount(item.closing)}</td>
                </tr>)}</tbody>
            </table></div>
        </details>}
    </section>;
}

/* ─── Строки ─── */

function BalanceLine({leg}: {leg: FinanceActionLeg}): JSX.Element {
    return <div className="fa-bl">
        <span className="fa-bo">{legOwnerLabel(leg)}</span>
        <span className="fa-bv"><LegRange leg={leg}/></span>
        <span className={`mono fa-dl${isPositive(leg.delta) ? " fa-pos" : ""}`}>{signed(leg.delta)}</span>
    </div>;
}

function ActionsFeed({items, busy, empty, onOpen}: {items: FinanceAction[]; busy: boolean; empty: string; onOpen: (action: FinanceAction) => void}): JSX.Element {
    const body: JSX.Element[] = [];
    let day = "";
    for (const action of items) {
        const current = financeDay(action.occurred_at);
        if (current !== day) { day = current; body.push(<tr className="fa-day" key={`day:${current}`}><td colSpan={6}>{formatFinanceDayTitle(action.occurred_at)}</td></tr>); }
        body.push(<ActionRow key={action.id} action={action} onOpen={onOpen}/>);
    }
    return <div className={`table-wrapper fa-feed${busy ? " fa-busy" : ""}`} aria-busy={busy}><table className="finance-actions-table">
        <thead><tr><th>Время</th><th>Действие</th><th>Кто выполнил</th><th className="fa-num">Сумма, USDT</th><th>Чей баланс · было → стало</th><th>Состояние</th></tr></thead>
        <tbody>{body.length ? body : <tr><td colSpan={6}><div className="empty-state">{empty}</div></td></tr>}</tbody>
    </table></div>;
}

function ActionRow({action, onOpen}: {action: FinanceAction; onOpen: (action: FinanceAction) => void}): JSX.Element {
    const view = actionView(action);
    const click = (event: React.MouseEvent<HTMLTableRowElement>): void => {
        const selection = window.getSelection();
        if (selection && !selection.isCollapsed && selection.anchorNode && event.currentTarget.contains(selection.anchorNode)) return;
        onOpen(action);
    };
    const keyDown = (event: React.KeyboardEvent<HTMLTableRowElement>): void => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onOpen(action); }
    };
    return <tr className="fa-row" tabIndex={0} role="button" aria-label={`Открыть карточку: ${view.title}`} onClick={click} onKeyDown={keyDown}>
        <td data-l="Время" className="mono fa-tm">{formatFinanceTime(action.occurred_at)}</td>
        <td data-l="Действие"><div className="fa-act"><BadgeView badge={view.badge}/><b>{view.title}</b></div>{view.sub && <small>{view.sub}</small>}</td>
        <td data-l="Кто выполнил">{view.who}{view.whoSub && <small>{view.whoSub}</small>}</td>
        <td data-l="Сумма, USDT" className="fa-num"><b className="mono">{view.amount}</b>{view.amountSub && <small>{view.amountSub}</small>}</td>
        <td data-l="Чей баланс · было → стало">{view.legs.map((leg) => <BalanceLine key={`${leg.owner.type}:${leg.owner.id}`} leg={leg}/>)}{view.balanceSub && <small className={view.balanceSubTone ? `fa-${view.balanceSubTone}-text` : undefined}>{view.balanceSub}</small>}</td>
        <td data-l="Состояние"><div className="fa-st"><PillView pill={view.state}/>{view.flags.map((flag) => <PillView key={flag.text} pill={flag}/>)}</div></td>
    </tr>;
}

/* ─── Карточка ─── */

function Section({title, children}: {title: string; children: React.ReactNode}): JSX.Element {
    return <section className="fa-sec"><h4>{title}</h4>{children}</section>;
}
function InfoRow({label, children, total = false}: {label: React.ReactNode; children: React.ReactNode; total?: boolean}): JSX.Element {
    return <div className={`fa-info-row${total ? " total" : ""}`}><span>{label}</span><strong>{children}</strong></div>;
}

function stepOf(action: FinanceAction, externalID?: string): {leg: FinanceActionLeg; step: FinanceActionStep} | undefined {
    if (!externalID) return undefined;
    for (const leg of action.legs) for (const step of leg.steps) if (bookExternalID(step) === externalID) return {leg, step};
    return undefined;
}

function Story({action, view}: {action: FinanceAction; view: ActionView}): JSX.Element {
    const merchant = action.legs.find((leg) => leg.owner.type === "merchant");
    const team = action.legs.find((leg) => leg.owner.type === "team");
    const legSentence = (leg: FinanceActionLeg): React.ReactNode => <>Баланс: {legOwnerLabel(leg).toLowerCase().startsWith("команда") ? "команда" : legOwnerLabel(leg).split(" ")[0].toLowerCase()} {ownerName(leg)} — <b>{signed(leg.delta)}</b>{debtNow(leg)}. </>;
    const legs = <>{action.legs.map((leg) => <React.Fragment key={`${leg.owner.type}:${leg.owner.id}`}>{legSentence(leg)}</React.Fragment>)}</>;
    switch (action.kind) {
    case "payment":
    case "platform_closed": {
        const order = action.order;
        const tb = action.team_book;
        const side = view.badge.tone;
        const rub = order?.fiat ? <b>{formatFinanceRub(order.fiat)}</b> : "рубли";
        return <div className="fa-story">
            {side === "in"
                ? <>Клиент перевёл {rub} на карту трейдера{order?.trader ? ` ${order.trader}` : ""} — рубли идут вне системы. </>
                : <>Клиенту мерчанта {ownerName(merchant)} нужно было выплатить {rub}. Трейдер{order?.trader ? ` ${order.trader}` : ""} перевёл их со своей карты — рубли идут вне системы. </>}
            {merchant && <>Баланс мерчанта {ownerName(merchant)} изменился на <b>{signed(merchant.delta)} USDT</b>{debtNow(merchant)}. </>}
            {tb?.status === "posted" && <>По книге Paylord команде {tb.team || ownerName(team)} начислено <b>{signed(tb.amount)}</b>{tb.reconciled ? " — сходится с мерчантом. " : `, разница ${amount(tb.difference)}. `}</>}
            {tb?.status === "waiting" && <>Книга Paylord по этой заявке ещё не пришла: баланс команды пока не изменён. </>}
            {!tb && team && <>Команде {ownerName(team)} — <b>{signed(team.delta)}</b>. </>}
            <b>Через наши кошельки USDT не проходили.</b>
        </div>;
    }
    case "team_withdrawal": {
        const w = action.team_withdrawal;
        const gap = w ? difference(w.reset || action.amount, w.received) : undefined;
        return <div className="fa-story">Команда {ownerName(team)} вывела <b>{amount(w?.requested || action.amount)} USDT</b>. С баланса команды списано {amount(w?.reset || action.amount)}, на адрес команды пришло <b>{amount(w?.received)}</b>.
            {action.notes.includes("underpaid") && <> Разница <b>{amount(gap)}</b> не объяснена поступлениями — повторно не списывать.</>}
            {action.notes.includes("overpaid") && <> Пришло больше списанного на {amount(difference(w?.received, w?.reset || action.amount))}.</>}
            {action.notes.includes("closed_manually") && <> Запрос закрыт вручную.</>}
        </div>;
    }
    case "merchant_withdrawal": {
        const w = action.merchant_withdrawal;
        return <div className="fa-story">Мерчант {ownerName(merchant)} выводит <b>{amount(w?.debit || action.amount)} USDT</b>{w ? ` в сети ${w.network.toUpperCase()}` : ""}. {view.state.text}.{w?.received && <> Получено {amount(w.received)}{w.fee ? `, комиссия ${amount(w.fee)}` : ""}.</>} {legs}</div>;
    }
    case "blockchain_deposit":
        return <div className="fa-story">{merchant ? `Мерчант ${ownerName(merchant)}` : `Команда ${ownerName(team)}`} пополнил баланс: <b>{amount(action.amount)} USDT</b> пришли в сети и зачислены автоматически.</div>;
    case "paylord_withdrawal":
        if (action.paylord_fee?.withdrawals.length) {
            const fee = action.paylord_fee.withdrawals;
            return <div className="fa-story">При выводе из кабинета {ownerName(merchant)} Paylord удерживает комиссию: на кошелёк кабинета приходит меньше, чем ушло из кабинета. Эта проводка закрывает разницу: <b>{amount(action.amount)} USDT</b>{` за ${feeWithdrawals(fee)}`}{fee.length > 1 && ` (у ${fee.slice(1).map((n) => `№${n}`).join(", ")} своей проводки не было)`}. {legs}</div>;
        }
        return <div className="fa-story">{view.sub ? `${view.title}: ${view.sub}. ` : `${view.title}. `}{legs}{action.notes.includes("net_zero") && <>Заморозка снята — итог 0.</>}</div>;
    default:
        return <div className="fa-story">{view.title}{view.sub ? ` · ${view.sub}` : ""}. {legs}</div>;
    }
}

function BalancesSection({action}: {action: FinanceAction}): JSX.Element {
    const viaBook = action.team_book?.status === "posted";
    return <Section title="Изменения балансов · наш учёт">
        <div className="fa-card">{action.legs.map((leg) => <div className="fa-balrow" key={`${leg.owner.type}:${leg.owner.id}`}>
            <span className="fa-who">{legOwnerLabel(leg)}{viaBook && leg.owner.type === "team" && <small>по книге Paylord</small>}</span>
            {leg.contiguous || leg.unknown || leg.chain_broken
                ? <span className="fa-ch"><LegRange leg={leg} format={exact}/></span>
                : <span className="fa-ch mono fa-steps">{leg.steps.map((step) => <span key={step.movement_id}>{step.before !== undefined && step.after !== undefined ? `${exact(money(step.before, leg))} → ${exact(money(step.after, leg))}` : "—"} ({exactSigned(money(step.delta, leg))})</span>)}<small>между шагами были другие операции</small></span>}
            <span className={`fa-d mono${isPositive(leg.delta) ? " fa-pos" : ""}`}>{exactSigned(leg.delta)}</span>
        </div>)}</div>
    </Section>;
}

function BookSection({action}: {action: FinanceAction}): JSX.Element | null {
    const rows = action.book_rows || [];
    const tb = action.team_book;
    if (!rows.length && !tb) return null;
    return <Section title="Книга Paylord · внешняя">
        <div className="fa-card">
            {rows.map((row) => { const found = stepOf(action, row.external_id); return <InfoRow key={row.id} label={<>{row.operation_type || "Строка книги"}<small>{[row.external_id && `строка ${row.external_id}`, row.operation_at && formatFinanceMoment(row.operation_at)].filter(Boolean).join(" · ")}</small></>}>
                <span className="mono">{found ? exactSigned(money(found.step.delta, found.leg)) : "—"}</span>
            </InfoRow>; })}
            {tb?.status === "posted" && tb.amount && <InfoRow label={`Итого по книге${tb.team ? ` · ${tb.team}` : ""}`} total><span className="mono">{exactSigned(tb.amount)}</span></InfoRow>}
            {tb?.status === "posted" && <InfoRow label="Сверка с мерчантом">{tb.reconciled ? <span className="fa-pill fa-ok">сходится</span> : <span className="fa-pill fa-warn">расходится</span>}{tb.difference && <span className="mono fa-muted"> остаток {exactSigned(tb.difference)}</span>}</InfoRow>}
            {tb?.status === "waiting" && <InfoRow label="Строки по заявке"><span className="fa-pill fa-need">ещё не пришли</span></InfoRow>}
        </div>
        {tb?.status === "posted" && <small className="fa-note">Книга начисляет команде столько, сколько списано с мерчанта. Остаток меньше 0,001 — округление площадки.</small>}
    </Section>;
}

const SLICE_COLORS = ["var(--fa-teal)", "var(--purple)", "var(--accent)", "var(--yellow)", "var(--green)", "var(--red)"];

function WalletSection({action}: {action: FinanceAction}): JSX.Element | null {
    if (action.kind === "payment" || action.kind === "platform_closed") {
        return <Section title="Наши кошельки"><div className="fa-card"><InfoRow label="Переводы USDT">нет — рубли шли с карты трейдера</InfoRow></div></Section>;
    }
    if (action.kind === "team_withdrawal" && action.team_withdrawal) {
        const w = action.team_withdrawal;
        const reset = w.reset || action.amount;
        const gap = difference(reset, w.received);
        const team = action.legs.find((leg) => leg.owner.type === "team");
        const slices = new Map<string, {name: string; atomic: bigint}>();
        for (const step of team?.steps || []) {
            const key = step.counterparty?.id || "—";
            const old = slices.get(key) || {name: step.counterparty?.name || step.counterparty?.id || "Мерчант не определён", atomic: 0n};
            try { old.atomic += -BigInt(step.delta); } catch { /* keep */ }
            slices.set(key, old);
        }
        const list = Array.from(slices.values()).filter((s) => s.atomic > 0n).sort((a, b) => (b.atomic > a.atomic ? 1 : b.atomic < a.atomic ? -1 : 0));
        const sum = list.reduce((s, x) => s + x.atomic, 0n);
        const scale = team?.delta.scale ?? 6;
        return <>
            <Section title="Путь денег">
                <div className="fa-path">
                    <div className="fa-pnode fa-ledger"><span className="fa-cap2">Списано с баланса {ownerName(team)}</span><small>наш учёт</small><b className="mono">{amount(reset)}</b></div>
                    {gap && gap.atomic !== "0" && <div className={`fa-parrow${isPositive(gap) ? " warn" : ""}`}><span className="fa-loss mono">{isPositive(gap) ? `−${amount(gap)}` : `+${amount(gap).replace("−", "")}`}</span>{isPositive(gap) ? "основание разницы не подтверждено" : "пришло больше списанного"}</div>}
                    <div className="fa-parrow">перевод в сети</div>
                    <div className="fa-pnode"><span className="fa-cap2">Пришло на адрес команды <span className="fa-tag">не наш</span></span><small>по данным сети</small><b className="mono">{amount(w.received)}</b></div>
                </div>
                <small className="fa-note">Запрошено {amount(w.requested)}. Переводы с хешами — в карточке операции.</small>
            </Section>
            {list.length > 0 && <Section title="Откуда заработано · по мерчантам">
                <div className="fa-card fa-slices">
                    <div className="fa-slicebar">{list.map((s, i) => <i key={s.name} style={{width: `${sum > 0n ? Number((s.atomic * 10000n) / sum) / 100 : 0}%`, background: SLICE_COLORS[i % SLICE_COLORS.length]}}/>)}</div>
                    {list.map((s, i) => <InfoRow key={s.name} label={<><i className="fa-sw" style={{background: SLICE_COLORS[i % SLICE_COLORS.length]}}/>{s.name}</>}><span className="mono">{exact({atomic: s.atomic.toString(), scale})}</span></InfoRow>)}
                    <InfoRow label="Итого" total><span className="mono">{exact({atomic: sum.toString(), scale})}</span></InfoRow>
                </div>
                <small className="fa-note">На каких мерчантах команда заработала выводимые деньги. Мерчанты при выводе ничего не платят.</small>
            </Section>}
        </>;
    }
    if (action.kind === "merchant_withdrawal" && action.merchant_withdrawal) {
        const w = action.merchant_withdrawal;
        return <Section title="Наши кошельки">
            <div className="fa-card">
                <InfoRow label="Списано с баланса мерчанта"><span className="mono">{amount(w.debit)}</span></InfoRow>
                {w.fee && <InfoRow label="Комиссия"><span className="mono">{amount(w.fee)}</span></InfoRow>}
                {w.received && <InfoRow label="Получено"><span className="mono">{amount(w.received)}</span></InfoRow>}
                <InfoRow label="Сеть">{w.network.toUpperCase()}</InfoRow>
                <InfoRow label="Адрес мерчанта"><span className="mono fa-break">{w.address}</span></InfoRow>
                {w.tx_hash && <InfoRow label="Транзакция"><span className="mono fa-break">{w.tx_hash}</span></InfoRow>}
                {w.rejection_reason && <InfoRow label="Причина отказа">{w.rejection_reason}</InfoRow>}
            </div>
        </Section>;
    }
    if (action.kind === "blockchain_deposit") {
        return <Section title="Наши кошельки"><div className="fa-card"><InfoRow label="Поступление в сети"><span className="mono">{signed(action.amount)}</span></InfoRow></div><small className="fa-note">Адреса и транзакция — в карточке операции.</small></Section>;
    }
    if (action.kind === "paylord_withdrawal") {
        return <Section title="Наши кошельки"><div className="fa-card"><InfoRow label="Поступление из кабинета">ищется в карточке операции</InfoRow></div><small className="fa-note">Возможные поступления не связаны автоматически: связь подтверждается вручную.</small></Section>;
    }
    return null;
}

interface Step { at?: string; text: string; tone: "ok" | "warn" | ""; }

function Timeline({action}: {action: FinanceAction}): JSX.Element | null {
    const order = action.order;
    const steps: Step[] = [];
    let title = "Ход заявки";
    if (order) {
        steps.push({at: order.created_at, text: "Заявка создана", tone: "ok"});
        if (order.claimed_at) steps.push({at: order.claimed_at, text: `Взял трейдер ${order.trader}${order.team ? ` · ${order.team}` : ""}`, tone: "ok"});
        if (order.appealed_at) steps.push({at: order.appealed_at, text: `Спор${order.appeal_reason ? `: ${order.appeal_reason}` : ""}`, tone: "warn"});
        steps.push({at: action.occurred_at, text: action.kind === "platform_closed" ? "Площадка закрыла заявку — баланс изменён" : "Платёж успешен — баланс мерчанта изменён", tone: "ok"});
        if (action.team_book?.status === "posted") steps.push({at: action.team_book.posted_at, text: "Книга Paylord провела сторону команды", tone: "ok"});
        if (action.team_book?.status === "waiting") steps.push({text: "Книга Paylord по заявке ещё не пришла", tone: "warn"});
    } else if (action.team_withdrawal) {
        const w = action.team_withdrawal;
        title = "Ход запроса";
        steps.push({at: w.created_at, text: `Запрос создал ${w.created_by_name || w.created_by}`, tone: "ok"});
        steps.push(w.completed_at ? {at: w.completed_at, text: `Завершил ${w.completed_by_name || w.completed_by || "—"}${action.notes.includes("closed_manually") ? " вручную" : ""}`, tone: action.notes.includes("underpaid") ? "warn" : "ok"} : {text: "Запрос ещё не завершён", tone: "warn"});
    } else if (action.merchant_withdrawal) {
        const w = action.merchant_withdrawal;
        title = "Ход запроса";
        steps.push({at: w.created_at, text: "Запрос вывода создан", tone: "ok"});
        steps.push({at: w.updated_at, text: `Состояние: ${actionState(action).text.toLowerCase()}`, tone: w.status === "completed" ? "ok" : "warn"});
    } else return null;
    steps.sort((a, b) => (a.at && b.at ? Date.parse(a.at) - Date.parse(b.at) : a.at ? -1 : b.at ? 1 : 0));
    return <Section title={title}><ul className="fa-timeline">{steps.map((s, i) => <li key={i} className={s.tone}><span className="fa-tt mono">{s.at ? formatFinanceMoment(s.at) : "—"}</span>{s.text}</li>)}</ul></Section>;
}

function Basis({action}: {action: FinanceAction}): JSX.Element {
    const items: {label: string; id: string; extra?: string; value?: string}[] = [{label: "Строка ДДС", id: action.id}];
    if (action.order_number) items.push({label: `Заявка${action.order ? ` ${action.order.side.toUpperCase()}` : ""}`, id: action.order_number});
    if (action.payment_id) items.push({label: "Платёж", id: action.payment_id});
    if (action.team_withdrawal) items.push({label: `Запрос вывода команды · ${action.team_withdrawal.status}`, id: action.team_withdrawal.request_id});
    if (action.merchant_withdrawal) items.push({label: `Запрос вывода мерчанта · ${action.merchant_withdrawal.status}`, id: action.merchant_withdrawal.request_id});
    for (const row of action.book_rows || []) items.push({label: `Строка книги Paylord · ${row.operation_type || "—"}`, id: row.external_id || row.id, extra: [row.external_order_id && `заявка ${row.external_order_id}`, row.external_withdrawal_id && `вывод ${row.external_withdrawal_id}`].filter(Boolean).join(" · ") || undefined});
    for (const leg of action.legs) for (const step of leg.steps) items.push({label: `Проводка · ${movementReasonLabel(step.reason)} · ${ownerName(leg)}`, id: step.movement_id, extra: step.operation_key, value: exactSigned(money(step.delta, leg))});
    if (action.actor) items.push({label: "Исполнитель (actor)", id: action.actor});
    return <details className="fa-fold"><summary>Основания <span>· {items.length} {plural(items.length, ["запись", "записи", "записей"])}</span></summary>
        <ul>{items.map((item, i) => <li key={`${item.id}:${i}`}><span>{item.label}</span><code>{item.id}{item.extra && <><br/>{item.extra}</>}</code>{item.value && <span className="fa-v mono">{item.value}</span>}</li>)}</ul>
    </details>;
}

export function ActionDrawer({action, onClose, onOpenOperation}: {action: FinanceAction; onClose: () => void; onOpenOperation: (operationID: string) => void}): JSX.Element {
    const panel = useFinanceDialog(onClose);
    const view = actionView(action);
    const token = financeTokenLabel(action.amount.token) || "USDT";
    const meta = [
        `${formatFinanceDate(action.occurred_at)} МСК`,
        view.sub,
        view.whoSub ? `${view.who} · ${view.whoSub}` : view.who,
        action.order?.rate && `курс ${formatFinanceAmount({atomic: String(Math.round(Number(action.order.rate) * 100)), scale: 2})} ₽`,
    ].filter(Boolean) as string[];
    return <div className="details-backdrop fa-backdrop" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
        <aside ref={panel} className="details-drawer fa-drawer" role="dialog" aria-modal="true" aria-label={`Карточка: ${view.title}`}>
            <header className="details-header">
                <div className="details-title-row fa-title-row">
                    <BadgeView badge={view.badge} large/>
                    <div className="details-title-main">
                        <div className="fa-kicker">{view.title}</div>
                        <div className="details-amount-line"><strong>{view.amount}</strong><span>{token}</span><PillView pill={view.state}/>{view.flags.map((flag) => <PillView key={flag.text} pill={flag}/>)}</div>
                        <div className="details-meta-line">{meta.map((m) => <span key={m}>{m}</span>)}</div>
                    </div>
                </div>
                <button type="button" className="details-close" aria-label="Закрыть карточку" onClick={onClose}>✕</button>
            </header>
            <div className="details-scroll fa-scroll">
                <Story action={action} view={view}/>
                <BalancesSection action={action}/>
                <BookSection action={action}/>
                <WalletSection action={action}/>
                <Timeline action={action}/>
                <Basis action={action}/>
                {action.operation_id && <div className="fa-go"><button type="button" className="btn btn-ghost btn-sm" onClick={() => onOpenOperation(action.operation_id as string)}>Карточка операции с источниками →</button></div>}
            </div>
        </aside>
    </div>;
}
